"use client";

import { memo, useCallback, useMemo } from "react";
import type { MeiliThought } from "@/generated/fastapi";
import { PROFILE_SHAPING_LABELS } from "../../config/actions";
import type { SearchHitRendererProps } from "@/features/search";
import { ThoughtItem } from "@/features/thoughts";
import { useUserChoices } from "@/state/globalStore";
import { useSituationsQuery } from "@/features/situations";

type NeedsAnchorThoughtHitProps = SearchHitRendererProps<MeiliThought>;

function NeedsAnchorThoughtHit({
  hit,
  config,
  onAnchorAction,
  onDeleteSearchResultAction,
  onUpdateSearchResultAction,
}: NeedsAnchorThoughtHitProps) {
  const { isSelectedThought, toggleThoughtSelection } = useUserChoices();
  const situationsQuery = useSituationsQuery();
  const thought = hit as MeiliThought;
  const validSituationIds = useMemo(
    () => new Set((situationsQuery.data ?? []).map((situation) => situation.id)),
    [situationsQuery.data],
  );
  const needsAnchor =
    !thought.situation_id ||
    (situationsQuery.isSuccess && !validSituationIds.has(thought.situation_id));

  const handleThoughtReassign = useCallback(
    async () => {
      onAnchorAction?.(hit);
    },
    [hit, onAnchorAction],
  );
  const handleToggleSelectThought = useCallback(() => {
    toggleThoughtSelection({
      prototypeCategory: thought.prototype,
      situationId: thought.situation_id,
      thoughtId: thought.objectID,
    });
  }, [
    thought.objectID,
    thought.prototype,
    thought.situation_id,
    toggleThoughtSelection,
  ]);

  return (
    <ThoughtItem
      config={config}
      hit={hit}
      onDeleteSearchResultAction={(thoughtId) =>
        onDeleteSearchResultAction?.(thoughtId)
      }
      onUpdateSearchResultAction={onUpdateSearchResultAction}
      selection={{
        isSelected: isSelectedThought(thought.objectID),
        onToggle: handleToggleSelectThought,
        humanReadable: "Select thought for anchoring",
      }}
      anchorAction={{
        onClick: handleThoughtReassign,
        icon: "anchor",
        humanReadable: needsAnchor
          ? "Anchor thought to a situation"
          : "Anchor thought to another situation",
        actionDescription: PROFILE_SHAPING_LABELS.anchorToSituation,
        color: "moveon.primary",
      }}
      primaryAction={{
        onClick: handleThoughtReassign,
        icon: "anchor",
        humanReadable: "Anchor thought to a situation",
        actionDescription: "Anchor",
        color: "moveon.primary",
      }}
    />
  );
}

export default memo(NeedsAnchorThoughtHit);
